import React, {useCallback, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {UserNameTag} from "../general/UserNameTag";
import axios from "axios";
import {Tooltip} from "flowbite-react";
import {TailoredMarkdown} from "../docs/TailoredMarkdown";
import {useAuth} from "../auth/AuthHook";
import {ArtenLogoIcon, UserIcon} from "../components/Icons";
import {withBase} from "../utils/basePath";
import {FloatingDots} from "../components/FloatingDots";

export enum CaseMessageType {
    USER_MESSAGE,
    MESSAGE_RESPONSE,
    NO_RESPONSE,
}

export enum CaseMessageRole {
    REQUEST = 0,
    RESPONSE = 1,
    SUMMARY = 2,
    ESCALATION = 3,
    ESCALATION_RESPONSE = 4,
    TEACHING = 5,
    NO_RESPONSE = 6
}

export type CaseMessage = {
    text: string
    messageType: CaseMessageType
    messageRole: CaseMessageRole
    requestedBy?: string
    timeToRespond?: number
    creationTime?: string
    attachment_id?: string;
    isLoading?: boolean
}

export type NoResponse = {
    messageType: CaseMessageType.NO_RESPONSE,
    messageRole: CaseMessageRole.NO_RESPONSE,
    reason_code: string,
    displayable_reason: string
    creationTime?: string
}

export function isNoResponse(m: CaseMessage | NoResponse): m is NoResponse {
    return m.messageType === CaseMessageType.NO_RESPONSE
}

interface ConversationProps {
    messages: ((CaseMessage|NoResponse)[] | null),
    caseId: string | null,
    jiraProjectKey?: string,
    onVersionChange: (product: string, version: string) => void,
    onAllVersionSelected?: ((allVersions: {product: string, version: string}[]) => void)
    isSyncing: boolean,
    lastMessageRef?: React.RefObject<HTMLDivElement>
}

export function Conversation({messages, caseId, onVersionChange, onAllVersionSelected, isSyncing, lastMessageRef}: ConversationProps) {
    const {t} = useTranslation()
    const {getHeaders} = useAuth()
    const [fileNameMap, setFileNameMap] = useState(new Map<string, string>())

    const handleFileDownload = useCallback(async (attachment_id: string) => {
        const response = await axios.get(withBase(`api/cases/${caseId}/attachments/${attachment_id}/data`), {
            headers: {
                ...await getHeaders(),
                "Content-Type": "application/json"
            },
            responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = response.headers['content-disposition'].split("filename=")[1];
        a.click();
        window.URL.revokeObjectURL(url);
    }, [caseId, getHeaders])

    useEffect(() => {
        const fetchAttachmentNames = async () => {
            if (messages == null) {
                return
            }
            const attachmentIds = messages.filter(m => !isNoResponse(m)).map(m => (m as CaseMessage).attachment_id).filter(id => id !== undefined) as string[]
            const attachmentNames = await Promise.all(attachmentIds.map(async id => {
                    const resp = await axios.get(withBase(`api/cases/${caseId}/attachments/${id}`), {
                        headers: {
                            ...await getHeaders(),
                            "Content-Type": "application/json"
                        }
                    })
                    return [id, resp.data["file_name"]] as [string, string]
                })
            )
            setFileNameMap(new Map(attachmentNames))
        }
        fetchAttachmentNames()
    }, [messages, caseId, getHeaders]);
    const lastItem = messages != null && messages.length > 0 ? messages[messages.length - 1] : undefined
    const noResponse = lastItem !== undefined && isNoResponse(lastItem) ? lastItem : undefined
    const realMessages: CaseMessage[] = messages?.filter((m): m is CaseMessage => !isNoResponse(m)) ?? []
    const appearingMessages = [...realMessages, ...(noResponse ? [noResponse] : [])]

    return (
        <div id="conversation-container" className="flex flex-col w-full items-center">
            <div id="conversation-inner-container" className="flex flex-col w-full items-center">
                <div id="div-to-scroll" className="grid grid-cols-[auto_1fr] gap-x-2 pl-10 pr-14 max-w-[850px] w-full">
                    {appearingMessages.map((cm, index) => {
                        const isLast = index === appearingMessages.length - 1;

                        return (
                            <React.Fragment key={`message${index}`}>
                                <div className="row-span-2 pt-4" id={"actor-icon"} ref={isLast ? lastMessageRef : null}>
                                    {
                                        cm.messageType === CaseMessageType.USER_MESSAGE ? <UserIcon/> : <ArtenLogoIcon/>
                                    }
                                </div>
                                <div className="flex items-center px-2 pt-4 leading-none" id={"actor-name"}>
                                    {
                                        cm.messageType === CaseMessageType.USER_MESSAGE ? (
                                                <div className="flex flex-row justify-start items-center space-x-2">
                                                    <UserNameTag
                                                        userId={cm.requestedBy}
                                                        extraClassNames="!text-neutral-500 font-normal"
                                                    />
                                                </div>
                                            ) :
                                            (
                                                <div className="text-blue-enov/80 font-normal">
                                                    Arten {cm.messageRole == CaseMessageRole.ESCALATION_RESPONSE ? `(${t("escalation tag")})` : ""}
                                                </div>
                                            )
                                    }
                                </div>
                                {isNoResponse(cm) && (
                                    <div key={`message${index}`} className="flex flex-row w-full gap-2 px-2 items-center text-gray-300 text-sm italic">
                                        {cm.displayable_reason}
                                    </div>
                                )}
                                { !isNoResponse(cm) && (
                                    <React.Fragment key={`message${index}`}>
                                    <div className="overflow-x-auto pb-2" id={"message-content"}>
                                        <TailoredMarkdown
                                            className={`px-2 text-gray-300 py-1 w-fit bg-gray-light-dark rounded-xl space-y-2 overflow-x-auto leading-relaxed ${cm.messageRole == CaseMessageRole.ESCALATION_RESPONSE ? "bg-gray-dark-enov" : ""}`}
                                            onVersionChange={isLast ? onVersionChange : null}
                                            onAllVersionSelected={isLast ? onAllVersionSelected : undefined}
                                        >
                                            {cm.text}
                                        </TailoredMarkdown>
                                        {cm.isLoading === true && (
                                            <FloatingDots/>
                                        )}
                                        <div
                                            className={`flex flex-row justify-end space-x-2 w-full px-8 justify-items-center`}>
                                            {cm.attachment_id !== undefined && cm.attachment_id !== null ? (
                                                <button id="download-attachment"
                                                        className={`flex flex-row space-x-1`}
                                                        onClick={(e) => handleFileDownload(cm.attachment_id ?? "")}>
                                                    <div className="text-gray-300 text-xs">
                                                        {fileNameMap.get(cm.attachment_id)}
                                                    </div>
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                                                         viewBox="0 0 24 24"
                                                         strokeWidth="1.5"
                                                         stroke="currentColor" className="w-3 h-3">
                                                        <path strokeLinecap="round" strokeLinejoin="round"
                                                              d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13"/>
                                                    </svg>
                                                </button>
                                            ) : ""}

                                            {cm.timeToRespond != null ? (
                                                <Tooltip content={t("Time to respond")}
                                                         className={`text-gray-300 text-xs`}>
                                                    <div
                                                        className={`text-gray-300 text-xs ${"pt-[.35rem]" /**could not make it aligned with export button otherwise**/}`}>
                                                        {`${Math.round(cm?.timeToRespond as number)}s`}
                                                    </div>
                                                </Tooltip>
                                            ) : ""}
                                        </div>
                                    </div>
                                </React.Fragment>)
                                }
                            </React.Fragment>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}